import type { Chamado, ChamadoLocal, RespostaPublica } from "../types";
import { naoVistasDo } from "./badge";

/**
 * Quem avisar, e o que o balão diz (DEV-216).
 *
 * A badge é o degrau mais passivo da escada de interrupção: ela informa que HÁ algo, e
 * quem não estava olhando para o canto do launcher — num site que nem é o nosso — não
 * fica sabendo de nada. O balão sobe um degrau e entrega a mensagem em si; por isso mora
 * aqui a decisão do QUE ele mostra, longe do `ui/widget.ts`, que não tem cobertura (o
 * shadow é `mode: "closed"`), como já acontece com `badge.ts` e `thread.ts`.
 *
 * Duas regras de escolha que o módulo carrega:
 *
 * 1. **Não é delta, é "ainda não avisei".** A tentação é comparar a contagem antes/depois
 *    do tick e avisar quando ela sobe. Mas o widget é destruído e recriado a cada
 *    navegação no site anfitrião, e nesse mundo "antes" nasce zerado: o delta ou dispara
 *    em toda página (a mesma resposta velha republicada sem parar) ou nunca dispara na
 *    primeira sincronização — justamente a que carrega a resposta que chegou enquanto a
 *    pessoa estava fora. Aqui a pergunta é outra: há resposta não vista que ESTA ABA ainda
 *    não anunciou? O que dá a resposta é `anunciados` (sessionStorage, no widget), e o que
 *    o carimbo guarda é o TOTAL do servidor — assim uma resposta nova depois do aviso
 *    muda o número e volta a avisar, e um F5 não reabre o que já foi mostrado.
 * 2. **Só fala do time.** A thread inclui as mensagens do próprio cliente (`doReporter`),
 *    e avisar alguém da mensagem que ela acabou de mandar é ruído puro.
 */

/**
 * Teto do texto que sai do módulo. O corte que a pessoa vê são as 3 linhas do CSS; este
 * aqui existe porque o texto vira HTML numa página que não é nossa — um comentário de
 * 4.000 caracteres não pode virar um nó gigante só para ficar escondido no `overflow`.
 */
const MAX_PREVIA = 280;

/** O que o balão precisa saber. Sem o título do chamado de propósito: quem já sabe do que
    se trata reconhece pelo código, e o assunto voltaria a comer as linhas da mensagem. */
export interface AvisoDeResposta {
  codigo: string;
  /** Primeiro nome de quem respondeu — o prefixo da frase, como em grupo de mensagem. */
  autor: string;
  /** A prévia, já em linha única e cortada no teto. */
  texto: string;
  /** ISO de quando a resposta foi escrita — o balão avisa tanto do que chegou agora quanto
      do que chegou enquanto a pessoa estava fora, e a linha de meta separa os dois. */
  quando: string;
  /** Total de respostas do servidor no instante do aviso: é a chave do "já anunciei". */
  respostas: number;
}

/**
 * O chamado que merece o balão agora, ou `null`.
 *
 * Com mais de um candidato vence o de resposta mais recente — um balão por vez, porque
 * empilhar avisos sobre a tela do cliente é exatamente o que um widget hóspede não faz. Os
 * outros continuam contados na badge e na linha "Meus chamados", que é onde a lista mora.
 */
export function chamadoParaAvisar(
  locais: ChamadoLocal[],
  remotos: Chamado[],
  anunciados: Record<string, number>,
): Chamado | null {
  const localPorCodigo = new Map(locais.map((l) => [l.codigo, l]));
  const candidatos = remotos.filter((r) => {
    const local = localPorCodigo.get(r.codigo);
    // Sem o par local não há o que comparar (nem token para buscar o detalhe depois).
    if (!local) return false;
    if (naoVistasDo(local, r) === 0) return false;

    return anunciados[r.codigo] !== r.respostas;
  });

  return candidatos.reduce<Chamado | null>((maisNovo, c) => {
    return maisNovo === null || instanteDe(c) > instanteDe(maisNovo) ? c : maisNovo;
  }, null);
}

/** Quando a conversa mexeu pela última vez. Chamado sem resposta cai na abertura — ele não
    chega aqui pela regra de não-vistas, mas a comparação não pode depender disso. */
function instanteDe(c: Chamado): string {
  return c.ultimaRespostaEm ?? c.abertoEm;
}

/**
 * Monta o aviso a partir do DETALHE do chamado (o resumo não traz texto nenhum).
 *
 * `null` quando não há o que mostrar: thread só com falas do cliente, ou uma resposta do
 * time sem texto e sem anexo. Balão vazio é pior que balão nenhum — a badge já disse que
 * há novidade, e um aviso mudo só gasta a atenção que ele existe para pedir.
 */
export function avisoDoChamado(c: Chamado): AvisoDeResposta | null {
  const thread = c.respostasThread ?? [];
  let ultima: RespostaPublica | null = null;
  // De trás para a frente: interessa a ÚLTIMA fala do time, não a primeira.
  for (let i = thread.length - 1; i >= 0; i--) {
    const r = thread[i];
    if (r && !r.doReporter) {
      ultima = r;
      break;
    }
  }
  if (ultima === null) return null;

  const texto = previaDaResposta(ultima);
  if (texto === "") return null;

  return {
    codigo: c.codigo,
    /* Rótulo do time não se encurta: "O time de Suporte" viraria "O". Quem decide é o
       SERVIDOR (`autorEhTime`) — o widget não tem como saber se o texto é um nome. */
    autor: ultima.autorEhTime === true ? ultima.autorNome : primeiroNome(ultima.autorNome),
    texto,
    quando: ultima.criadoEm,
    respostas: c.respostas,
  };
}

/**
 * A mensagem em uma linha só.
 *
 * O espaço em branco é colapsado porque a resposta do time vem com as quebras que ela tem
 * na thread, e num balão de três linhas cada `\n` gastaria uma delas sem dizer nada. A
 * mensagem que só traz imagem vira frase — "enviou uma imagem" — em vez de sair vazia: é
 * uma resposta de verdade, e o que ela informa é que há algo para ver lá dentro.
 */
export function previaDaResposta(r: RespostaPublica): string {
  const texto = r.texto.replace(/\s+/g, " ").trim();
  if (texto !== "") return texto.slice(0, MAX_PREVIA);

  const imagens = r.anexos?.length ?? 0;
  if (imagens === 0) return "";

  return imagens === 1 ? "enviou uma imagem" : `enviou ${imagens} imagens`;
}

/** O primeiro nome de quem respondeu. Cai em "Suporte" — o mesmo fallback do servidor —
    quando o nome vem vazio: no balão, sem nome nenhum a frase começa por dois-pontos. */
export function primeiroNome(nome: string): string {
  const primeiro = nome.trim().split(/\s+/)[0] ?? "";

  return primeiro === "" ? "Suporte" : primeiro;
}
